import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "../../App.css";
import { apiPostAuth, apiPostAuthSlow } from "../../utils/api";
import languages from "../../utils/languages.json";

export default function AutoReplySimulationPage() {
  const { package_name } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [developerPrompt, setDeveloperPrompt] = useState("");
  const [initialPrompt, setInitialPrompt] = useState("");
  const [credits, setCredits] = useState(0);
  const [sampleReview, setSampleReview] = useState("");
  const [rating, setRating] = useState(5);
  const [appVersion, setAppVersion] = useState("");
  const [language, setLanguage] = useState("en");
  const [replyOutput, setReplyOutput] = useState("");
  const [savingPrompt, setSavingPrompt] = useState(false);
  const [simulating, setSimulating] = useState(false);

  const [targetRatings, setTargetRatings] = useState({
    1: true,
    2: true,
    3: true,
    4: true,
    5: true,
  });
  const [initialTargetRatings, setInitialTargetRatings] = useState(null);
  const [savingTargetRating, setSavingTargetRating] = useState(false);

  const isPromptEdited = developerPrompt !== initialPrompt;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await apiPostAuth("google_apps/get_app_simulation_data.php", {
          app_package_name: package_name,
        });
        if (response.result === 1) {
          setDeveloperPrompt(response.developer_prompt || "");
          setInitialPrompt(response.developer_prompt || "");
          setCredits(response.google_play_credit || 0);

          // Set target ratings
          const ratings = {
            1: response.target_ratings.one_star_targeted === 1,
            2: response.target_ratings.two_star_targeted === 1,
            3: response.target_ratings.three_star_targeted === 1,
            4: response.target_ratings.four_star_targeted === 1,
            5: response.target_ratings.five_star_targeted === 1,
          };
          setTargetRatings(ratings);
          setInitialTargetRatings(ratings);
        } else {
          navigate(`/dashboard/google-play-review`);
        }
      } catch (error) {
        navigate(`/dashboard/google-play-review`);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [package_name, navigate]);

  const handleSavePrompt = async () => {
    setSavingPrompt(true);
    try {
      const response = await apiPostAuth("google_apps/update_developer_prompt.php", {
        app_package_name: package_name,
        developer_prompt: developerPrompt,
      });
      if (response.result === 1) {
        setInitialPrompt(developerPrompt);
        alert("Prompt saved successfully!");
      } else {
        alert(response.message || "Failed to save prompt.");
      }
    } catch (error) {
      alert("Something went wrong while saving the prompt.");
    } finally {
      setSavingPrompt(false);
    }
  };

  const handleSaveTargetRating = async () => {

    // Check if at least one rating is selected
    const isAnySelected = Object.values(targetRatings).some((value) => value);
    if (!isAnySelected) {
      alert("You must select at least one rating to target.");
      return;
    }

    setSavingTargetRating(true);
    try {
      const response = await apiPostAuth("google_apps/update_target_rating.php", {
        app_package_name: package_name,
        one_star_targeted: targetRatings[1] ? 1 : 0,
        two_star_targeted: targetRatings[2] ? 1 : 0,
        three_star_targeted: targetRatings[3] ? 1 : 0,
        four_star_targeted: targetRatings[4] ? 1 : 0,
        five_star_targeted: targetRatings[5] ? 1 : 0,
      });
      if (response.result === 1) {
        alert("Target ratings updated successfully!");
        setInitialTargetRatings({ ...targetRatings });
      } else {
        alert(response.message || "Failed to update target ratings.");
      }
    } catch (error) {
      alert("Something went wrong while updating target ratings.");
    } finally {
      setSavingTargetRating(false);
    }
  };


  const isTargetRatingsChanged = () => {
    if (!initialTargetRatings) return false;
    return Object.keys(targetRatings).some(
      (key) => targetRatings[key] !== initialTargetRatings[key]
    );
  };


  const handleSimulateResponse = async () => {
    if (!sampleReview.trim()) {
      alert("Please enter a sample review to simulate.");
      return;
    }
    if (!appVersion.trim()) {
      alert("Please enter the app version code.");
      return;
    }

    setSimulating(true);
    setReplyOutput("");
    try {
      const response = await apiPostAuthSlow("google_apps/simulate_response.php", {
        app_package_name: package_name,
        review: sampleReview,
        rating: rating,
        app_version: appVersion,
        language: language,
      });
      if (response.result === 1) {
        setReplyOutput(response.generated_reply || "");
        setCredits(response.new_available_credit);
      } else {
        alert("Failed to simulate response. " + response.message);
        setReplyOutput("");
      }
    } catch (error) {
      alert("Failed to simulate response. Try again.");
      setReplyOutput("");
    } finally {
      setSimulating(false);
    }
  };

  const handleGetCredits = () => {
    navigate("/dashboard/billing-payment");
  };

  const toggleStarTarget = (star) => {
    setTargetRatings((prev) => ({ ...prev, [star]: !prev[star] }));
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", marginTop: "50px" }}>
        <div className="loading-spinner"></div>
        <p>Loading simulation data...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: "20px" }}>
      <button
        onClick={() => navigate(`/dashboard/google-play-review/${package_name}`)}
        style={backButtonStyle}
      >
        ← Back to App Dashboard
      </button>

      <h1 style={{ fontSize: "1.8rem", marginBottom: "20px", color: "#1f2937" }}>
        Auto Reply Simulation
      </h1>

      {/* Credits Section */}
      <section style={sectionStyle}>
        <h2 style={sectionTitleStyle}>Credits</h2>
        <p style={{ fontSize: "1rem", margin: "5px 0" }}>
          <strong>Available Credits:</strong> {credits}
        </p>
        <button
          onClick={handleGetCredits}
          className="primary-button"
          style={{ padding: "6px 12px", fontSize: "0.9rem" }}
        >
          Get Credits
        </button>
        <p style={{ fontSize: "0.85rem", color: "#6b7280", marginTop: "8px" }}>
          This simulation will consume your available credits.
        </p>
      </section>

      {/* Target Ratings Section */}
      <section style={sectionStyle}>
        <h2 style={sectionTitleStyle}>Target Ratings</h2>
        <p style={{ fontSize: "0.9rem", color: "#4b5563", marginBottom: "10px" }}>
          Select which star ratings should receive automated replies. The Automation is replying all reviews by default.
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {[1, 2, 3, 4, 5].map((star) => (
            <label key={star} style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <input
                type="checkbox"
                checked={targetRatings[star]}
                onChange={() => toggleStarTarget(star)}
              />
              {star} Star{star > 1 ? "s" : ""} Rating
            </label>
          ))}
        </div>
        {isTargetRatingsChanged() && (
          <button
            onClick={handleSaveTargetRating}
            className="primary-button"
            style={{ marginTop: "10px" }}
            disabled={savingTargetRating}
          >
            {savingTargetRating ? "Saving..." : "Save Target Ratings"}
          </button>
          
        )}

        {isTargetRatingsChanged() && (
          <p style={{ fontSize: "0.85rem", color: "#6b7280", marginTop: "6px" }}>
            This changes will take effect immediately if the automation is running.
          </p>
          
        )}
        
        
      </section>


      {/* Developer Prompt Section */}
      <section style={sectionStyle}>
        <h2 style={sectionTitleStyle}>Developer Prompt (Optional)</h2>
        <textarea
          value={developerPrompt}
          onChange={(e) => setDeveloperPrompt(e.target.value)}
          rows="5"
          placeholder="Describe unique features, tone, or special instructions for AI responses"
          style={textareaStyle}
        />
        <p style={{ fontSize: "0.85rem", color: "#6b7280", marginTop: "4px" }}>
          This prompt customizes AI responses for your app.
          <strong> If left empty</strong>, a default generalized prompt is used.
        </p>

        {isPromptEdited && (
          <button
            onClick={handleSavePrompt}
            className="primary-button"
            style={{ marginTop: "10px" }}
            disabled={savingPrompt}
          >
            {savingPrompt ? "Saving..." : "Save Prompt"}
          </button>
        )}
      </section>

      {/* Sample Review */}
      <section style={sectionStyle}>
        <h2 style={sectionTitleStyle}>Sample Review</h2>
        <textarea
          value={sampleReview}
          onChange={(e) => setSampleReview(e.target.value)}
          rows="3"
          placeholder="Enter a sample user review here..."
          style={textareaStyle}
        />
        {/* Rating Selector */}
        <label
          htmlFor="rating"
          style={{ display: "block", marginTop: "12px", fontWeight: "500", color: "#1f2937" }}
        >
          Rating
        </label>
        <select
          id="rating"
          value={rating}
          onChange={(e) => setRating(parseInt(e.target.value))}
          style={{
            marginTop: "6px",
            padding: "8px",
            width: "50%",
            border: "1px solid #ccc",
            borderRadius: "6px",
            fontSize: "1rem",
          }}
        >
          {[1, 2, 3, 4, 5].map((star) => (
            <option key={star} value={star}>
              {star} Star{star > 1 ? "s" : ""}
            </option>
          ))}
        </select>

        {/* App Version Input */}
        <label
          htmlFor="appVersion"
          style={{ display: "block", marginTop: "12px", fontWeight: "500", color: "#1f2937" }}
        >
          App Version Code (build code)
        </label>
        <input
          id="appVersion"
          type="number"
          value={appVersion}
          onChange={(e) => setAppVersion(e.target.value)}
          placeholder="100"
          min="1"
          step="1"
          style={{
            marginTop: "6px",
            padding: "8px",
            width: "50%",
            border: "1px solid #ccc",
            borderRadius: "6px",
            fontSize: "1rem",
          }}
        />

        {/* Language Selector */}
        <label
          htmlFor="language"
          style={{ display: "block", marginTop: "12px", fontWeight: "500", color: "#1f2937" }}
        >
          Reviewer Device Language
        </label>
        <select
          id="language"
          value={language}
          onChange={(e) => setLanguage(e.target.value)}
          style={{
            marginTop: "6px",
            padding: "8px",
            width: "50%",
            border: "1px solid #ccc",
            borderRadius: "6px",
            fontSize: "1rem",
          }}
        >
          {languages.map((lang) => (
            <option key={lang.code} value={lang.code}>
              {lang.name} ({lang.code.toUpperCase()})
            </option>
          ))}
        </select>

        <button
          onClick={handleSimulateResponse}
          className="primary-button"
          style={{ marginTop: "15px", marginLeft: "15px" }}
          disabled={isPromptEdited || simulating}
        >
          {simulating ? "Simulating..." : "Simulate Response"}
        </button>
      </section>

      {/* Reply Output */}
      <section style={sectionStyle}>
        <h2 style={sectionTitleStyle}>Reply Output</h2>
        <textarea
          value={replyOutput}
          readOnly
          rows="4"
          placeholder="The AI-generated response will appear here..."
          style={{ ...textareaStyle, backgroundColor: "#f9fafb" }}
        />
      </section>
    </div>
  );
}

/* --- Styles --- */
const sectionStyle = {
  backgroundColor: "#fff",
  padding: "20px",
  borderRadius: "8px",
  boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
  marginBottom: "20px",
};

const sectionTitleStyle = {
  fontSize: "1.2rem",
  marginBottom: "10px",
  color: "#1f2937",
};

const textareaStyle = {
  width: "90%",
  padding: "10px",
  border: "1px solid #ccc",
  borderRadius: "6px",
  fontSize: "1rem",
  resize: "vertical",
};

const backButtonStyle = {
  display: "inline-block",
  padding: "8px 16px",
  backgroundColor: "#f2f6fc",
  color: "#2563eb",
  fontWeight: "500",
  border: "1px solid #d1d5db",
  borderRadius: "6px",
  cursor: "pointer",
  marginBottom: "20px",
  transition: "all 0.2s ease",
};
